import React from "react";
import {
  TextField,
  MenuItem,
  Grid,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";

const ProductDetailsForm = ({
  item,
  setItem,
  condition,
  conditionValue,
  itemCounter,
  setItemCounter,
  buttonColor,
  setButtonColor,
  priceArray,
  setPriceDisplay,
  setPriceHeader,
  setCalculate,
  t,
  deliveryCost,
  setDeliveryCost,
  setCostDelivery,
  setDiscountBonus,
}) => {
  return (
    <Box className='space-y-6'>
      <TextField
        fullWidth
        label={t("addProductPage:nameLabel")}
        name='title'
        value={item.title}
        onChange={(e) => setItem({ ...item, title: e.target.value })}
        variant='outlined'
        size='small'
      />

      <TextField
        fullWidth
        select
        label={t("addProductPage:conditionLabel")}
        value={item.condition || ""}
        onChange={(e) => {
          setItem({ ...item, condition: e.target.value });
        }}
        variant='outlined'
        size='small'>
        {condition.map((option) => (
          <MenuItem
            key={option.id}
            value={option.id}>
            {option.condition}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        fullWidth
        label={t("addProductPage:descLabel")}
        name='description'
        value={item.description}
        onChange={(e) => setItem({ ...item, description: e.target.value })}
        multiline
        minRows={4}
        variant='outlined'
        size='small'
      />

      <Grid
        container
        spacing={2}>
        <Grid
          item
          xs={12}
          sm={4}>
          <TextField
            fullWidth
            name='price'
            id='price'
            label={t("admin:price") || "Price"}
            type='number'
            value={item.price || ""}
            onChange={(e) => {
              setItem({ ...item, price: e.target.value });
              setItemCounter(0);
              priceArray.length = 0;
              setPriceDisplay([]);
              setPriceHeader("hidden");
              setCalculate({
                background: "bg-stone-300",
                text: "text-stone-400",
              });

              const discountInput = document.getElementById("discount")?.value;
              const buttonPlus =
                e.target.value !== "" && discountInput !== ""
                  ? "border-cromboOrange text-cromboOrange"
                  : "border-stone-400 text-stone-400";
              setButtonColor({
                minus: "border-stone-400 text-stone-400",
                plus: buttonPlus,
              });
            }}
            variant='outlined'
            size='small'
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm={4}>
          <Box position='relative'>
            <TextField
              fullWidth
              name='discount'
              id='discount'
              label={t("admin:bulkUnitDiscount") || "Bulk Unit Discount"}
              type='number'
              defaultValue='0'
              onChange={(e) => {
                setItemCounter(0);
                setPriceDisplay([]);
                setPriceHeader("hidden");
                setCalculate({
                  background: "bg-stone-300",
                  text: "text-stone-400",
                });

                const priceInput = document.getElementById("price")?.value;
                const buttonPlus =
                  e.target.value !== "" && priceInput !== ""
                    ? "border-cromboOrange text-cromboOrange"
                    : "border-stone-400 text-stone-400";
                setButtonColor({
                  minus: "border-stone-400 text-stone-400",
                  plus: buttonPlus,
                });
              }}
              variant='outlined'
              size='small'
              InputProps={{
                endAdornment: <InputAdornment position='end'>%</InputAdornment>,
              }}
            />
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          sm={4}>
          <TextField
            fullWidth
            label={t("admin:quantity") || "Quantity"}
            type='number'
            value={itemCounter}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <IconButton
                    onClick={() => {
                      const priceInput =
                        document.getElementById("price")?.value;
                      const discountInput =
                        document.getElementById("discount")?.value;
                      if (itemCounter === 0 || !priceInput || !discountInput)
                        return;

                      const newCount = itemCounter - 1;
                      priceArray.pop();
                      setItemCounter(newCount);
                      setPriceDisplay([...priceArray]);
                      if (newCount === 0) {
                        setPriceHeader("hidden");
                        setCalculate({
                          background: "bg-stone-300",
                          text: "text-stone-400",
                        });
                      }
                      setButtonColor({
                        minus: "border-stone-400 text-stone-400",
                        plus: "border-stone-400 text-stone-400",
                      });
                    }}
                    size='small'>
                    <RemoveIcon fontSize='small' />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    onClick={() => {
                      const priceInput =
                        document.getElementById("price")?.value;
                      const discountInput =
                        document.getElementById("discount")?.value;
                      if (!priceInput || !discountInput) return;

                      const newCount = itemCounter + 1;
                      priceArray.push(newCount);
                      setItemCounter(newCount);
                      setPriceDisplay([...priceArray]);
                      setPriceHeader("visible");
                      setButtonColor({
                        minus: "border-stone-400 text-stone-400",
                        plus: "border-stone-400 text-stone-400",
                      });
                    }}
                    size='small'>
                    <AddIcon fontSize='small' />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            variant='outlined'
            size='small'
          />
        </Grid>
      </Grid>

      <Grid
        container
        spacing={2}>
        <Grid
          item
          xs={12}
          sm={6}>
          <TextField
            fullWidth
            type='number'
            min='0'
            id='bonusDiscount'
            label={t("productPage:bonusDiscountLabel") || "Bonus Discount"}
            defaultValue='0'
            onChange={(e) => setDiscountBonus(e.target.value)}
            variant='outlined'
            size='small'
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm={6}>
          <TextField
            fullWidth
            type='number'
            id='delivery'
            label={t("admin:deliveryPriceLabel") || "Delivery Price"}
            value={deliveryCost}
            onChange={(e) => {
              const val = e.target.value;
              setDeliveryCost(val); // allow empty string
              setCostDelivery(val); // ensure backend gets the value

              const discount =
                Number(document.getElementById("discount")?.value) || 0;
              const price =
                Number(document.getElementById("price")?.value) || 0;
              if (discount && price && Number(val) && itemCounter !== 0) {
                setCalculate({
                  background: "bg-cromboOrange",
                  text: "text-white",
                });
              } else {
                setCalculate({
                  background: "bg-stone-300",
                  text: "text-stone-400",
                });
              }
            }}
            variant='outlined'
            size='small'
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProductDetailsForm;
